<template>
  <Suspense>
    <div>
      <MeridiaToastContainer />
      <div
        v-if="appStore.loading"
        role="status"
        class="bg-slate-200 w-full min-h-screen overflow-hidden flex flex-nowrap items-center justify-center gap-4"
      >
        <h1 class="text-large font-bold mr-1 text-center">
          {{ $t('app.loading') }}
        </h1>
        <MeridiaIcon
          name="progress_activity"
          type="material-symbols-outlined"
          class="icon-l animate-spin"
          aria-hidden="true"
        />
      </div>

      <div
        v-else-if="errorDetails"
        :key="errorDetails.type"
        role="alert"
        class="w-full min-h-screen overflow-hidden"
      >
        <FatalError
          :title="$t('fatal_error.title')"
          :subtitle="$t('fatal_error.sub_500')"
          :error-details="errorDetails"
        />
      </div>
      <transition v-else name="fade" mode="out-in" :duration="150">
        <component :is="route.meta.layout" v-if="route.meta.layout" />
        <div v-else :key="key" class="relative w-full min-h-screen">
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in" :duration="150">
              <component :is="Component" />
            </transition>
          </router-view>
        </div>
      </transition>
    </div>
    <template #fallback>{{ $t('app.loading') }}</template>
  </Suspense>
</template>

<script setup lang="ts">
import { reactive, ref, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { until } from '@vueuse/core';
import { useAppStore } from 'src/core/stores/app';
import FatalError from 'src/core/components/FatalError/FatalError.vue';
import { useAnalyticsStore } from 'src/core/stores/analytics/index';

const route = reactive(useRoute());
const key = ref(route.path);

const { t } = useI18n();
const appStore = useAppStore();
const { errorDetails, loading, initialized } = storeToRefs(appStore);
appStore.initialize();
appStore.t = t;

// track all page loads
const analyticsStore = useAnalyticsStore();
watch(route, () => {
  analyticsStore.page(String(route.name), {
    title: String(route.meta.title) ?? undefined,
  });
});

onMounted(async () => {
  await until(initialized)
    .toBe(true, {
      timeout: 30000,
      throwOnTimeout: true,
    })
    .catch(() => {
      loading.value = false;
      errorDetails.value = {
        message: t('app.request_tracking.errors.failed_to_initialize'),
        type: 'INITIALIZATION_ERROR',
      };
    });
});
</script>

<style lang="scss">
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease-out;
}
</style>
